/**
 * shared/space_operation_rules.ts — CLIENT MIRROR of server/src/lib/space_operation_rules.ts (space campaign slice H,
 * 2026-10-01; the server copy is authoritative — keep them byte-identical below the headers; verify:space-ops compares).
 * OPERATIONS WITH OBJECTIVES: the state machine, the frozen allocations, the evidence ids, the route beacons, the
 * refuge, the extraction path and its dwell, the escort's roster state, the one settlement posting, the board's view.
 * Pure.
 */
import { warH } from './space_stations';
import { fortOf, battleCentre, ESCORT_KEY, HULLS_PER_FRIGATE, HULLS_PER_CORVETTE, type EscortClass } from './space_campaign';
import { warFrame, warLineD, type V3 } from './war_authority';
import { splitReward, termsShares, type ShareIn } from './space_economy';
import { workCredit, beatTooSoon } from './space_survey';

// ── THE ROLLOUT ──────────────────────────────────────────────────────────────
/** Read at a FUNDING (the operation's rules) and at a campaign's OPENING (whether its escorts may be extracted). */
export const OPS_FLAG = 'space_operations_v1';
/** The operation rules an operation was funded under / a campaign pinned (0 = before them). */
export const OPS_RULES_VERSION = 1;
/** An operation's escrow is a ledger wallet of its own: Identity(OP_ESCROW_BASE + opId) (the guild vaults are 0x6a11…,
 *  the banks 0xba4c…, the survey desks 0x5e5e…). Its balance IS the money the operation still holds. */
export const OP_ESCROW_BASE = 0x0e5c_0000_0000n;
export function opEscrowBits(opId: bigint): bigint { return OP_ESCROW_BASE + opId; }

// ── THE STATE MACHINE ────────────────────────────────────────────────────────
/** DRAFT (terms written, the court has not funded) → FUNDED (the escrow holds the money) → OPEN (enrolment and supply
 *  open) → ACTIVE (the war is engaged: every objective live) → CLOSING (the war's deadline passed: no new evidence,
 *  waiting for its due history) → SETTLED (paid once, the rest refunded). A cancellation settles from any live state. */
export const OP_STATES = ['draft', 'funded', 'open', 'active', 'closing', 'settled'] as const;
export type OpState = (typeof OP_STATES)[number];
const OP_EDGES: Record<OpState, readonly OpState[]> = {
  draft: ['funded', 'settled'],
  funded: ['open', 'settled'],
  open: ['active', 'closing', 'settled'],
  active: ['closing', 'settled'],
  closing: ['settled'],
  settled: [],
};
export function canMove(from: string, to: string): boolean {
  return (OP_STATES as readonly string[]).includes(from) && (OP_EDGES[from as OpState] as readonly string[]).includes(to);
}
/** Why an operation settled. */
export type SettleCause = 'completed' | 'cancelled' | 'unfunded' | 'no_war';
/** What the sweep knows about an operation's war (the reducer gathers it; the step is pure). */
export interface OpClock {
  state: OpState;
  motion: 'open' | 'passed' | 'failed' | 'lapsed' | 'none';
  /** The war of the operation's cycle: absent until the sweep opens it. */
  campaign: 'none' | 'engaged' | 'retreat' | 'resolved';
  nowS: number;
  /** The deadline: the war's endsAt when it exists (a retreat pulls it), else the window's end. */
  cutoffS: number;
  /** The window's own end (cyc·3600 + 1800): with no war by then, none will open. */
  windowEndS: number;
}
/** The next state on the clock alone (null = stay). Settlement of a war's operations happens inside the war's own
 *  resolution (the due history is complete there), never here — except an operation whose war never opened. */
export function clockStep(c: OpClock): { to: OpState; cause?: SettleCause } | null {
  switch (c.state) {
    case 'draft':
      if (c.motion === 'failed' || c.motion === 'lapsed') return { to: 'settled', cause: 'unfunded' };
      return null;
    case 'funded': return { to: 'open' };
    case 'open':
      if (c.campaign === 'engaged' || c.campaign === 'retreat') return c.nowS >= c.cutoffS ? { to: 'closing' } : { to: 'active' };
      if (c.campaign === 'resolved') return { to: 'closing' };
      if (c.nowS >= c.windowEndS) return { to: 'settled', cause: 'no_war' };
      return null;
    case 'active':
      if (c.nowS >= c.cutoffS || c.campaign === 'resolved') return { to: 'closing' };
      return null;
    default: return null;
  }
}

// ── THE TEMPLATES AND THE FROZEN ALLOCATIONS (report §21.8) ─────────────────
export const OP_TEMPLATES = ['scout', 'haul', 'repair', 'combat'] as const;
export type OpTemplate = (typeof OP_TEMPLATES)[number];
export const TEMPLATE_LABEL: Record<OpTemplate, string> = {
  scout: 'Scout verification', haul: 'Supply delivery', repair: 'Eligible repair', combat: 'Screen + extraction',
};
export interface OpAlloc { scout: number; haul: number; repair: number; combat: number }
/** The report's §5C fixture: 1,000 SOV — scout 100, hauler 400, engineer 250, screen / extraction 250. */
export const OP_FIXTURE: OpAlloc = { scout: 100, haul: 400, repair: 250, combat: 250 };
export const OP_ALLOC_MAX = 100_000;
export const OP_TOTAL_MIN = 10;
/** Three funded kit applications share the repair allocation (83 / 83 / 84 of 250). */
export const REPAIR_APPLICATIONS = 3;
/** Two distinct screen contributions; the rest of the combat allocation is the extraction's. */
export const SCREEN_SLOTS = 2;
/** A guild runs at most this many live operations at once. */
export const OPS_PER_GUILD = 2;
export function opTotal(a: OpAlloc): number { return a.scout + a.haul + a.repair + a.combat; }
/** Why an allocation cannot be funded (null = it can). */
export function allocationProblem(a: OpAlloc): string | null {
  for (const t of OP_TEMPLATES) {
    const v = a[t];
    if (!Number.isInteger(v) || v < 0 || v > OP_ALLOC_MAX) return `${TEMPLATE_LABEL[t]}: 0 … ${OP_ALLOC_MAX} ⓒ, whole credits`;
  }
  if (a.repair > 0 && a.repair < REPAIR_APPLICATIONS) return `Eligible repair: at least ${REPAIR_APPLICATIONS} ⓒ (one per application) or none`;
  if (a.combat > 0 && a.combat < 5) return 'Screen + extraction: at least 5 ⓒ (two screen slots and the extraction) or none';
  if (opTotal(a) < OP_TOTAL_MIN) return `An operation funds at least ${OP_TOTAL_MIN} ⓒ`;
  return null;
}
/** N shares of `total`: equal floors, the integer remainder to the LAST shares in stable application order (the
 *  report's rule: 250 over three applications is 83, 83, 84). The shares always sum to `total`. */
export function applicationShares(total: number, n: number): number[] {
  if (n <= 0) return [];
  const t = Math.max(0, Math.floor(total));
  const base = Math.floor(t / n);
  const out = new Array<number>(n).fill(base);
  let left = t - base * n;
  for (let i = n - 1; left > 0; i--, left--) out[i]++;
  return out;
}
/** The combat allocation: two screen slots of floor(40 % / 2) each, the extraction the rest (250 → 50 / 50 / 150). */
export function combatSplit(alloc: number): { screenSlot: number; extraction: number } {
  const a = Math.max(0, Math.floor(alloc));
  const slot = Math.floor(Math.floor((a * 2) / 5) / SCREEN_SLOTS);
  return { screenSlot: slot, extraction: a - slot * SCREEN_SLOTS };
}
/** Every payable slot of an objective with its FROZEN amount — fixed at funding, never re-priced. */
export interface OpSlot { slot: string; amount: number }
export function templateSlots(t: OpTemplate, a: OpAlloc): OpSlot[] {
  const v = a[t];
  if (v <= 0) return [];
  if (t === 'repair') return applicationShares(v, REPAIR_APPLICATIONS).map((amount, i) => ({ slot: `repair#${i + 1}`, amount }));
  if (t === 'combat') {
    const c = combatSplit(v);
    return [...Array.from({ length: SCREEN_SLOTS }, (_, i) => ({ slot: `screen#${i + 1}`, amount: c.screenSlot })), { slot: 'extract', amount: c.extraction }];
  }
  return [{ slot: t, amount: v }];
}
export function slotTotal(slots: readonly OpSlot[]): number { return slots.reduce((n, s) => n + s.amount, 0); }

// ── EVIDENCE (every id is another system's receipt) ─────────────────────────
export const EVIDENCE_RE = /^(scout:\d{1,20}|port:\d{1,20}|patch:[0-9a-f]{64}:[A-Za-z0-9:_-]{1,48}|shot:[0-9a-f]{64}:(gun|missile):\d{1,24}|extract:\d{1,20}:war[01]:(fri|cor)\d{1,2})$/;
export function receiptKey(opId: bigint, template: OpTemplate, evidenceId: string): string { return `${opId}:${template}:${evidenceId}`; }
/** Which template an evidence id may serve. */
export function evidenceTemplate(evidenceId: string): OpTemplate | null {
  if (evidenceId.startsWith('scout:')) return 'scout';
  if (evidenceId.startsWith('port:')) return 'haul';
  if (evidenceId.startsWith('patch:')) return 'repair';
  if (evidenceId.startsWith('shot:') || evidenceId.startsWith('extract:')) return 'combat';
  return null;
}

// ── THE AUTHORED ROUTE BEACONS (the scout's objective) ──────────────────────
/** Two flank markers per war, 18 km across the war frame from the fort's battle centre — well off the walls (the
 *  capitals stand ≥ 13.5 km out ALONG the axis) and inside the 26 km war zone. Named for the fort. */
export const ROUTE_ACROSS_M = 18_000;
export const SCOUT_RANGE_M = 220;
export const SCOUT_VERIFY_MS = 5_000;
export interface Beacon { key: string; name: string; pos: V3 }
export function routeBeacons(cyc: number): [Beacon, Beacon] {
  const fort = fortOf(cyc), c = battleCentre(fort), f = warFrame(cyc);
  const at = (s: number): V3 => ({ x: c[0] + f.az.x * s * ROUTE_ACROSS_M, y: c[1] + 300, z: c[2] + f.az.z * s * ROUTE_ACROSS_M });
  return [
    { key: `route:${cyc}:port`, name: `the ${fort.name} port-flank marker`, pos: at(1) },
    { key: `route:${cyc}:starboard`, name: `the ${fort.name} starboard-flank marker`, pos: at(-1) },
  ];
}
/** The beacon an operation's scout verifies (one per operation — hashed so two guilds' scouts spread). */
export function opBeacon(cyc: number, opId: bigint): Beacon { return routeBeacons(cyc)[Number(opId % 2n)]; }

// ── THE REFUGE AND THE ESCORT'S STATION (§21.9) ─────────────────────────────
/** A side's refuge: behind its frigate line, short of its capitals, in the gap between the wall's centre column and
 *  the next (≥ 1.2 km clear of a capital's hull box), on that side's own flank. */
export const REFUGE_R_M = 300;
export const REFUGE_BACK_M = 3_000;
export const REFUGE_ACROSS_M = 2_600;
const dist = (a: V3, b: V3): number => Math.sqrt((a.x - b.x) * (a.x - b.x) + (a.y - b.y) * (a.y - b.y) + (a.z - b.z) * (a.z - b.z));
export function refugePoint(cyc: number, side: 0 | 1, tWar: number): V3 {
  const c = battleCentre(fortOf(cyc)), f = warFrame(cyc), sgn = side === 0 ? 1 : -1;
  const along = sgn * (warLineD(tWar) - REFUGE_BACK_M), across = sgn * REFUGE_ACROSS_M;
  return { x: c[0] + f.ax.x * along + f.az.x * across, y: c[1], z: c[2] + f.ax.z * along + f.az.z * across };
}
/** An escort's STATION — the renderer's own formula (SpaceModule.updateWarEscorts; verify:space-ops pins the text):
 *  frigates a line 5 km inside their wall, corvettes the near band of the furball. The server knows nothing closer: an
 *  escort is client-flown until its extraction makes its path the server's. */
export function escortStation(cyc: number, tWar: number, side: 0 | 1, cls: EscortClass, slot: number): V3 {
  const c = battleCentre(fortOf(cyc)), f = warFrame(cyc), sgn = side === 0 ? 1 : -1;
  const key = `war${side}:${ESCORT_KEY[cls]}${slot}`;
  const along = cls === 'frigate' ? warLineD(tWar) - 5000 - slot * 500 : 3200 + warH(`${key}a`) * 1800;
  const across = cls === 'frigate' ? (slot - 1) * 2400 : (slot - 2.5) * 1500;
  return { x: c[0] + f.ax.x * sgn * along + f.az.x * across, y: c[1] + (warH(`${key}y`) - 0.5) * 600, z: c[2] + f.ax.z * sgn * along + f.az.z * across };
}

// ── THE EXTRACTION (a declared course, then the exposed dwell) ──────────────
/** The extracting escort flies a straight course from its station to its side's refuge at this speed (frozen at the
 *  start and stored — every client draws the same hull on the same path; the server knows where it is). */
export const EXTRACT_SPEED_MPS = 120;
/** Ten seconds of VALID dwell inside the refuge at ≤ 5 m/s — the escort and the escorting pilot both. */
export const EXTRACT_DWELL_MS = 10_000;
export const DWELL_MAX_MPS = 5;
/** Screen evidence: a validated hit by an enrolled pilot whose row is within this of the extracting asset. */
export const SCREEN_RADIUS_M = 2_500;
export interface ExtractionPath { sx: number; sy: number; sz: number; rx: number; ry: number; rz: number; startS: number; arriveS: number }
export function extractionPath(start: V3, refuge: V3, startS: number): ExtractionPath {
  const L = dist(start, refuge);
  return { sx: start.x, sy: start.y, sz: start.z, rx: refuge.x, ry: refuge.y, rz: refuge.z, startS, arriveS: startS + L / EXTRACT_SPEED_MPS };
}
/** Where the extracting escort is at `nowS`: on the course, then held at the refuge (speed 0). */
export function extractionPose(p: ExtractionPath, nowS: number): { pos: V3; speed: number; arrived: boolean } {
  const span = p.arriveS - p.startS;
  const k = span <= 0 ? 1 : Math.max(0, Math.min(1, (nowS - p.startS) / span));
  const pos = { x: p.sx + (p.rx - p.sx) * k, y: p.sy + (p.ry - p.sy) * k, z: p.sz + (p.rz - p.sz) * k };
  const arrived = k >= 1;
  return { pos, speed: arrived || nowS < p.startS ? 0 : EXTRACT_SPEED_MPS, arrived };
}
export interface DwellMeter { accMs: number; lastBeatMs: number; lastX: number; lastY: number; lastZ: number; seq: number }
export interface DwellFacts { nowMs: number; pilot: V3; seq: number; escort: { pos: V3; speed: number }; refuge: V3; escortAlive: boolean }
export interface MeterStep<M> { next: M; credited: number; reason: string; done: boolean; reset: boolean }
/** One dwell beat (≤ 1 Hz). The escort and the pilot must both be inside the refuge at ≤ 5 m/s (the pilot's speed is
 *  its displacement over the beat — the server keeps no velocities); leaving the refuge RESETS the incomplete dwell
 *  (report §21.8); a warp / dock (the location seq) resets it; a gap longer than F's pause credits nothing. */
export function dwellStep(m: DwellMeter | null, f: DwellFacts): MeterStep<DwellMeter> {
  const fresh = (acc: number): DwellMeter => ({ accMs: acc, lastBeatMs: f.nowMs, lastX: f.pilot.x, lastY: f.pilot.y, lastZ: f.pilot.z, seq: f.seq });
  if (!f.escortAlive) return { next: fresh(0), credited: 0, reason: 'the escort is lost — no extraction', done: false, reset: true };
  if (m && beatTooSoon(m.lastBeatMs, f.nowMs)) return { next: m, credited: 0, reason: 'too soon', done: false, reset: false };
  const pilotIn = dist(f.pilot, f.refuge) <= REFUGE_R_M, escortIn = dist(f.escort.pos, f.refuge) <= REFUGE_R_M;
  if (!escortIn) return { next: fresh(0), credited: 0, reason: 'the escort has not reached the refuge', done: false, reset: (m?.accMs ?? 0) > 0 };
  if (!pilotIn) return { next: fresh(0), credited: 0, reason: 'you are outside the refuge — the dwell starts again', done: false, reset: (m?.accMs ?? 0) > 0 };
  if (!m || m.seq !== f.seq) return { next: fresh(0), credited: 0, reason: m ? 'a warp or a dock broke the hold — the dwell starts again' : 'holding', done: false, reset: !!m && m.accMs > 0 };
  const dtS = (f.nowMs - m.lastBeatMs) / 1000;
  const pilotSpeed = dtS > 0 ? dist(f.pilot, { x: m.lastX, y: m.lastY, z: m.lastZ }) / dtS : Infinity;
  if (f.escort.speed > DWELL_MAX_MPS || pilotSpeed > DWELL_MAX_MPS) return { next: fresh(m.accMs), credited: 0, reason: `too fast for the dwell (${Math.round(pilotSpeed)} m/s — hold at ${DWELL_MAX_MPS} m/s or less)`, done: false, reset: false };
  const credit = workCredit(m.lastBeatMs, f.nowMs);
  const acc = Math.min(EXTRACT_DWELL_MS, m.accMs + credit);
  return { next: fresh(acc), credited: credit, reason: credit > 0 ? 'holding' : 'paused (a gap in the beats)', done: acc >= EXTRACT_DWELL_MS, reset: false };
}
export interface ScoutMeter { accMs: number; lastBeatMs: number; seq: number; shipId: string }
export interface ScoutFacts { nowMs: number; pilot: V3; beacon: V3; seq: number; shipId: string; losClear: boolean; ewCredit: (ms: number) => number }
/** One scout beat (≤ 1 Hz): within 220 m of the beacon with a clear line, F's metered credit slowed by I's interference.
 *  Accepted progress PERSISTS (re-entry resumes it — report §21.8); a warp / dock / a new hull life voids only the
 *  interval in progress. */
export function scoutStep(m: ScoutMeter | null, f: ScoutFacts): MeterStep<ScoutMeter> {
  const base = (acc: number): ScoutMeter => ({ accMs: acc, lastBeatMs: f.nowMs, seq: f.seq, shipId: f.shipId });
  if (m && beatTooSoon(m.lastBeatMs, f.nowMs)) return { next: m, credited: 0, reason: 'too soon', done: false, reset: false };
  const acc0 = m?.accMs ?? 0;
  if (dist(f.pilot, f.beacon) > SCOUT_RANGE_M) return { next: base(acc0), credited: 0, reason: `out of range — hold within ${SCOUT_RANGE_M} m of the marker`, done: false, reset: false };
  if (!f.losClear) return { next: base(acc0), credited: 0, reason: 'the line to the marker is blocked', done: false, reset: false };
  if (!m) return { next: base(0), credited: 0, reason: 'verifying', done: false, reset: false };
  if (m.seq !== f.seq || m.shipId !== f.shipId) return { next: base(acc0), credited: 0, reason: 'a warp, a dock or a new hull voided the interval in progress', done: false, reset: false };
  const credit = f.ewCredit(workCredit(m.lastBeatMs, f.nowMs));
  const acc = Math.min(SCOUT_VERIFY_MS, acc0 + credit);
  return { next: base(acc), credited: credit, reason: credit > 0 ? 'verifying' : 'paused (a gap in the beats)', done: acc >= SCOUT_VERIFY_MS, reset: false };
}

// ── THE ESCORT'S STATE ON THE ROSTER (§21.9) ────────────────────────────────
export const ESCORT_STATES = ['ACTIVE_ALIVE', 'DISABLED_ALIVE', 'EXTRACTING', 'WITHDRAWN_ALIVE', 'DEAD'] as const;
export type EscortState = (typeof ESCORT_STATES)[number];
/** A pinned war's escort whose server hull is at or below this fraction is DISABLED (alive — no death, no salvage). */
export const DISABLED_FRAC = 0.4;
export function escortStateOf(f: { alive: boolean; hp: number | null; hpMax: number | null; extraction: string | null }): EscortState {
  if (!f.alive) return 'DEAD';
  if (f.extraction === 'withdrawn') return 'WITHDRAWN_ALIVE';
  if (f.extraction === 'extracting') return 'EXTRACTING';
  if (f.hp !== null && f.hpMax !== null && f.hpMax > 0 && f.hp <= DISABLED_FRAC * f.hpMax) return 'DISABLED_ALIVE';
  return 'ACTIVE_ALIVE';
}
/** The escort loss pick and validated damage take ACTIVE, DISABLED and EXTRACTING (still exposed) — never WITHDRAWN or
 *  DEAD. A withdrawn escort is returned ONCE at settlement with the other survivors. */
export function lossEligible(s: EscortState): boolean { return s === 'ACTIVE_ALIVE' || s === 'DISABLED_ALIVE' || s === 'EXTRACTING'; }
export function extractable(s: EscortState): boolean { return s === 'ACTIVE_ALIVE' || s === 'DISABLED_ALIVE'; }
export function escortReturnHulls(cls: string): number { return cls === 'frigate' ? HULLS_PER_FRIGATE : HULLS_PER_CORVETTE; }
export const ESCORT_STATE_WORD: Record<EscortState, string> = {
  ACTIVE_ALIVE: 'in the fight', DISABLED_ALIVE: 'disabled (alive)', EXTRACTING: 'extracting — still exposed',
  WITHDRAWN_ALIVE: 'withdrawn — returns at settlement', DEAD: 'lost',
};

// ── THE SETTLEMENT (ONE posting per operation) ──────────────────────────────
export interface PlanReceipt { key: string; slot: string; beneficiary: string; amount: number; crew: readonly ShareIn[] }
export interface PlanLeg { key: string; sov: bigint; why: string }
export interface SettlementPlan { held: bigint; paid: bigint; refund: bigint; payees: PlanLeg[]; balanced: boolean }
/** Pay every ACCEPTED receipt its frozen amount (a crew split by E's largest remainder, the beneficiary first), refund
 *  the rest of the escrow to the guild vault. `held` is the escrow's balance. Balanced = the payees never exceed it. */
export function settlementPlan(held: bigint, receipts: readonly PlanReceipt[]): SettlementPlan {
  const payees: PlanLeg[] = [];
  let paid = 0n;
  for (const r of receipts) {
    const amt = BigInt(Math.max(0, Math.floor(r.amount)));
    if (amt <= 0n) continue;
    const parts = r.crew.length ? splitReward(amt, termsShares(r.beneficiary, r.crew)) : [{ key: r.beneficiary, sov: amt }];
    for (const p of parts) if (p.sov > 0n) { payees.push({ key: p.key, sov: p.sov, why: r.slot }); paid += p.sov; }
  }
  const balanced = paid <= held && held >= 0n;
  return { held, paid, refund: balanced ? held - paid : 0n, payees, balanced };
}
/** The posting's legs: the escrow −held, each payee +, the vault +refund — zero legs omitted; Σ = 0 by construction. */
export function settlementLegs(plan: SettlementPlan, escrowKey: string, vaultKey: string): Array<{ key: string; sov: bigint; why: string }> {
  const legs = [{ key: escrowKey, sov: -plan.held, why: 'escrow' }, ...plan.payees];
  if (plan.refund > 0n) legs.push({ key: vaultKey, sov: plan.refund, why: 'refund' });
  return legs.filter((l) => l.sov !== 0n);
}

// ── WHAT THE BOARD SHOWS (accepted, remaining, funded, expiry, the block) ───
export interface ObjFacts {
  template: OpTemplate; alloc: OpAlloc; opState: string; cutoffS: number; nowS: number;
  /** The receipts accepted for this objective (slot + frozen amount). */
  accepted: ReadonlyArray<{ slot: string; amount: number }>;
  /** The objective's own block, when the board knows one (e.g. "enrol first"), else ''. */
  block: string;
}
export interface ObjView { label: string; unit: string; accepted: number; total: number; remaining: number; earned: number; funded: number; expiresAtS: number; done: boolean; block: string }
export function objectiveView(f: ObjFacts): ObjView {
  const slots = templateSlots(f.template, f.alloc);
  const unit = f.template === 'scout' ? 'verification' : f.template === 'haul' ? 'manifest (24 u)' : f.template === 'repair' ? 'application' : 'contribution';
  const accepted = Math.min(slots.length, f.accepted.length);
  const earned = f.accepted.reduce((n, a) => n + a.amount, 0);
  const done = slots.length > 0 && accepted >= slots.length;
  let block = f.block;
  if (!slots.length) block = 'not funded in this operation';
  else if (done) block = '';
  else if (f.opState === 'draft') block = block || 'waiting for the court to fund it';
  else if (f.opState === 'closing') block = 'the war is over — no new evidence; waiting for the settlement';
  else if (f.opState === 'settled') block = 'settled';
  return { label: TEMPLATE_LABEL[f.template], unit, accepted, total: slots.length, remaining: Math.max(0, slots.length - accepted), earned, funded: slotTotal(slots), expiresAtS: f.cutoffS, done, block };
}
/** May this template take evidence in this operation state? (Supply hauling may begin before the war opens.) */
export function evidenceOpen(template: OpTemplate, state: string): boolean {
  if (template === 'haul') return state === 'open' || state === 'active';
  return state === 'active';
}
